/**
 * Architect Agent UI contracts — shared between chaos-gateway and chaos-ui.
 *
 * One "run" is a single ``/architect/dispatch`` call: founder hands the
 * agent a high-level task and the LangGraph workflow decomposes it into
 * child Kanban cards. The run is the audit envelope around that flow
 * (see migration 025_architect_runs.py for storage).
 *
 * The wire shape is intentionally Notion-flat: every node carries its
 * own state + timestamps so the workstream graph can render mid-run
 * without joining additional tables.
 */
import { z } from 'zod';

import { autonomyLevelSchema } from './kanban';

// ── Run status (envelope) ─────────────────────────────────────────────
// Mirrors chaos.architect_runs.status CHECK constraint.
export const architectRunStatuses = [
  'pending',
  'running',
  'succeeded',
  'failed',
  'cancelled',
] as const;
export const architectRunStatusSchema = z.enum(architectRunStatuses);
export type ArchitectRunStatus = z.infer<typeof architectRunStatusSchema>;

// ── Node state (per LangGraph node) ───────────────────────────────────
// Mirrors chaos.architect_run_nodes.state CHECK constraint.
export const architectNodeStates = ['pending', 'running', 'done', 'failed', 'skipped'] as const;
export const architectNodeStateSchema = z.enum(architectNodeStates);
export type ArchitectNodeState = z.infer<typeof architectNodeStateSchema>;

// ── Scope of a dispatch (what the decomposition attaches to) ──────────
export const architectScopes = ['project', 'board', 'card'] as const;
export const architectScopeSchema = z.enum(architectScopes);
export type ArchitectScope = z.infer<typeof architectScopeSchema>;

// ── Workflow node identifiers — canonical ordering for the UI graph ───
export const architectNodeNames = [
  'intake',
  'classify',
  'decompose',
  'skill_match',
  'routing',
  'plan_emit',
  'validation_gate',
  'dispatch',
  'monitor',
] as const;
export const architectNodeNameSchema = z.enum(architectNodeNames);
export type ArchitectNodeName = z.infer<typeof architectNodeNameSchema>;

// ── Node row ──────────────────────────────────────────────────────────
export const architectNodeSchema = z.object({
  id: z.string().uuid(),
  name: architectNodeNameSchema,
  position: z.number().int().nonnegative(),
  state: architectNodeStateSchema,
  startedAt: z.string().datetime().nullable(),
  endedAt: z.string().datetime().nullable(),
  inputJson: z.unknown().nullable(),
  outputJson: z.unknown().nullable(),
  logs: z.string().nullable(),
});
export type ArchitectNode = z.infer<typeof architectNodeSchema>;

// ── Decomposed card backlink (Kanban view) ────────────────────────────
export const architectDecomposedCardSchema = z.object({
  id: z.string().uuid(),
  boardId: z.string().uuid(),
  columnId: z.string().uuid(),
  title: z.string(),
  createdAt: z.string().datetime(),
});
export type ArchitectDecomposedCard = z.infer<typeof architectDecomposedCardSchema>;

// ── Run row ───────────────────────────────────────────────────────────
export const architectRunSchema = z.object({
  id: z.string().uuid(),
  workspaceId: z.string().uuid(),
  createdBy: z.string().uuid(),
  description: z.string(),
  scope: architectScopeSchema,
  scopeTargetId: z.string().uuid().nullable(),
  autonomyLevel: autonomyLevelSchema,
  parentCardId: z.string().uuid().nullable(),
  dryRun: z.boolean(),
  status: architectRunStatusSchema,
  errorMessage: z.string().nullable(),
  projectId: z.string().uuid().nullable(),
  planVersion: z.number().int().nullable(),
  startedAt: z.string().datetime(),
  endedAt: z.string().datetime().nullable(),
  // The full set of nodes — sparse if the run hasn't reached them yet
  // (gateway always returns the canonical 9-node skeleton in `position`
  // order, with `pending` for un-run nodes).
  nodes: z.array(architectNodeSchema),
  decomposedCardIds: z.array(z.string().uuid()),
});
export type ArchitectRun = z.infer<typeof architectRunSchema>;

// Light-weight row for the history table (no nodes / cards joined).
export const architectRunSummarySchema = architectRunSchema
  .omit({ nodes: true, decomposedCardIds: true })
  .extend({
    cardCount: z.number().int().nonnegative(),
  });
export type ArchitectRunSummary = z.infer<typeof architectRunSummarySchema>;

// ── Dispatch request (POST /api/architect/dispatch) ───────────────────
export const architectDispatchRequestSchema = z.object({
  description: z.string().min(8, 'Describe the task in at least 8 characters.').max(8_000),
  scope: architectScopeSchema.default('board'),
  scopeTargetId: z.string().uuid().nullable().optional(),
  autonomyLevel: autonomyLevelSchema.default('HITL'),
  parentCardId: z.string().uuid().nullable().optional(),
  // dryRun skips the LLM/Temporal hand-off; the workflow nodes still
  // record state transitions so the UI can demo the graph without
  // burning provider tokens (E2E + screencasts).
  dryRun: z.boolean().default(false),
});
export type ArchitectDispatchRequest = z.infer<typeof architectDispatchRequestSchema>;

export const architectDispatchAckSchema = z.object({
  runId: z.string().uuid(),
  status: architectRunStatusSchema,
  acceptedAt: z.string().datetime(),
});
export type ArchitectDispatchAck = z.infer<typeof architectDispatchAckSchema>;

// ── Run list (GET /api/architect/runs) ────────────────────────────────
export const architectRunListSchema = z.object({
  runs: z.array(architectRunSummarySchema),
  total: z.number().int().nonnegative(),
});
export type ArchitectRunList = z.infer<typeof architectRunListSchema>;

// ── WS event (chaos_architect_run_<runId> NOTIFY payload) ─────────────
export const architectWsEventSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('run'),
    event: z.enum(['INSERT', 'UPDATE']),
    runId: z.string().uuid(),
    status: architectRunStatusSchema,
    startedAt: z.string().datetime().nullable().optional(),
    endedAt: z.string().datetime().nullable().optional(),
  }),
  z.object({
    kind: z.literal('node'),
    event: z.enum(['INSERT', 'UPDATE']),
    runId: z.string().uuid(),
    name: architectNodeNameSchema,
    position: z.number().int(),
    state: architectNodeStateSchema,
    startedAt: z.string().datetime().nullable().optional(),
    endedAt: z.string().datetime().nullable().optional(),
  }),
  z.object({
    kind: z.literal('hello'),
    runId: z.string().uuid(),
  }),
  z.object({ kind: z.literal('ping') }),
]);
export type ArchitectWsEvent = z.infer<typeof architectWsEventSchema>;
